import { from, Observable, of, zip } from 'rxjs'
import { catchError, switchMap } from "rxjs/operators"

import { getTranslatedString, SearchResponse, ThesaurusTerm } from "@skinsoft/types";
import { thesaurusTermSelectors } from "@skinsoft/selectors";
import { dbCacheDown } from "../dexie/cache-down";
import { STATS_KEYS } from "../dexie/stats-keys";
import { OfflineDataFetcher } from "../offline-data-fetcher";

export class ThesaurusTermOfflineDataFetcher extends OfflineDataFetcher<ThesaurusTerm> {
    
    constructor() {
        super(dbCacheDown.thesaurusTerm, STATS_KEYS.thesaurusTerm)
    }

    public fetchChildren(id: string, locale: string, page: number = 0, size: number = 100, searchOnUnimarc: boolean = false): Observable<ThesaurusTerm[]> {
        return from(
            this.table.where('thesaurusTermSkin.genericTerm')
            .equals(id)
            .filter(child => {
                if(searchOnUnimarc) {
                    return thesaurusTermSelectors.isForUnimarc(child) === true
                }
                return !thesaurusTermSelectors.isForUnimarc(child)
            })
            .offset(size * page)
            .limit(size)
            .toArray()
        ) as Observable<ThesaurusTerm[]>
    }

    public searchOnRoot(thesaurusId: string, locale: string, searchText: string, includeChildren: boolean, page: number = 0, size: number = 100, excludedIds?: string[], includeUnimarc: boolean = false): Observable<SearchResponse<ThesaurusTerm>> {
        const lowerCaseSearchText = searchText.toLowerCase()
        
        const dexieCollection = this.table.where('thesaurusTermSkin.thesaurus')
            .equals(thesaurusId)
            .filter(thesaurusTerm => {
                if(excludedIds) {
                    if(excludedIds.includes(thesaurusTerm.id)) {
                        return false
                    }
                }

                if(!includeUnimarc) {
                    const isForUnimarc = thesaurusTermSelectors.isForUnimarc(thesaurusTerm)
                    if(isForUnimarc) {
                        return false
                    }
                }

                if(!includeChildren) {
                    const genericTerm = thesaurusTermSelectors.genericTerm(thesaurusTerm)
                    if(genericTerm) {
                        return false
                    }
                }

                const preferredTerm = thesaurusTermSelectors.preferredTerm(thesaurusTerm)
                // we need preferredTerm to be not null in order to be able to sort results later
                if(!preferredTerm) {
                    return false
                }

                if(searchText) {
                    const preferredTermValue = getTranslatedString(preferredTerm, locale)
                    if(preferredTermValue && preferredTermValue.toLowerCase().includes(lowerCaseSearchText)) {
                        return true
                    }

                    const synonyms = thesaurusTermSelectors.synonyms(thesaurusTerm)
                    if(synonyms && synonyms.length) {
                        for(const synonym of synonyms) {
                            const value = getTranslatedString(synonym, locale)
                            if(value && value.toLowerCase().includes(lowerCaseSearchText)) {
                                return true
                            }
                        }
                    }
                    return false
                }

                return true
            })

        return zip(
            from(dexieCollection.count()),
            from(
                dexieCollection
                    .offset(size * page)
                    .limit(size)
                    .sortBy(`thesaurusTermCS.preferredTerm.${locale}`)
            )
        ).pipe(
            switchMap(([nbResults, results]) => {
                return of({
                    results: results,
                    allData: results,
                    totalSize: nbResults
                } as SearchResponse<ThesaurusTerm>) 
            })
        )
    }
}